import React, { useState, useRef, useEffect } from "react";
import { Check, Sparkles, Paperclip, FileText, X, Lock, Edit3, ChevronDown, ChevronUp, AlertCircle } from "lucide-react";
import { Spinner, LLM_MODEL_OPTIONS } from "../constants";
import { STUDIO_DOC_TYPES } from "./studioConst";
import { SX, TX } from "./studioTokens";

const PLACEHOLDERS = {
  invoice: "e.g. Extract invoice number, vendor name, invoice date, line items, subtotal, tax and total amount…",
  purchase_order: "e.g. Extract PO number, vendor name, order date, line items with description, quantity, unit price, subtotal, tax and total amount…",
  contract: "e.g. Extract contract parties, effective date, term length, renewal clause, payment terms…",
  receipt: "e.g. Extract merchant name, date, items purchased, subtotal, tax, total and payment method…",
  custom: "Describe the fields and data points you want AI to extract from your document…",
  DEFAULT: "e.g. Extract key fields like names, dates, totals and line items — or leave this blank and AI will figure out what to extract on its own.",
};

const PREFILLS = {
  invoice: "Extract invoice number, vendor name, invoice date, line items, subtotal, tax and total amount.",
  purchase_order: "Extract PO number, vendor name, order date, line items with description, quantity, unit price, subtotal, tax and total amount.",
  contract: "Extract contract parties, effective date, term length, renewal clause and payment terms.",
  receipt: "Extract merchant name, date, items purchased, subtotal, tax, total and payment method.",
};

const DocTypePill = ({ opt, active, onClick }) => (
  <button onClick={onClick}
    className={`flex items-center gap-2 px-4 py-2.5 rounded-full text-[13.5px] whitespace-nowrap flex-shrink-0 transition-all ${
      active
        ? "bg-[#FBF5FA] border-[1.5px] border-[#A04A97] text-[#893F82] font-semibold"
        : `bg-white text-[#1A1A1A] font-medium border-[1.5px] ${opt.id === "custom" ? "border-dashed" : ""} border-[#ECE9EE] hover:border-[#E9D2E6]`
    }`}>
    {opt.label}
    {active && <Check size={13} />}
  </button>
);

const AdvancedOptions = ({ model, setModel }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    if (!open) return;
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, [open]);

  return (
    <div ref={ref} className="relative">
      <button onClick={() => setOpen((o) => !o)}
        className={`${SX.btn} border border-[#ECE9EE] text-[#1A1A1A] font-medium ${open ? "bg-[#F5F5F5]" : "bg-transparent"}`}>
        Advanced Options {open ? <ChevronUp size={13} className="text-[#52525B]" /> : <ChevronDown size={13} className="text-[#52525B]" />}
      </button>
      {open && (
        <div className={`absolute bottom-[46px] left-0 w-[270px] p-1.5 z-[50] ${SX.popover}`}>
          <div className="px-2.5 pt-1.5 pb-1 text-[10.5px] font-semibold tracking-[0.06em] text-[#8A8A90] uppercase">AI Model</div>
          {LLM_MODEL_OPTIONS.map((o) => (
            <div key={o.value} onClick={() => { setModel(o.value); setOpen(false); }}
              className="flex items-start gap-2.5 px-2.5 py-2.5 rounded-lg cursor-pointer hover:bg-[#F5F5F5]">
              <div className={`w-[17px] h-[17px] rounded-full border-[1.5px] flex items-center justify-center mt-px flex-shrink-0 ${model === o.value ? "border-[#A04A97]" : "border-[#ECE9EE]"}`}>
                {model === o.value && <div className="w-[9px] h-[9px] rounded-full bg-[#A04A97]" />}
              </div>
              <div className="text-[13px] font-semibold text-[#1A1A1A]">{o.label}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

/* 02 · Template Builder — the AI-first entry point. Doc type, a plain-language
   instruction, and a sample document; everything else is inferred. */
const GenerateCard = ({ onGenerate, isGenerating, error, onManual }) => {
  const [docType, setDocType] = useState(null);
  const [instruction, setInstruction] = useState("");
  const [file, setFile] = useState(null);
  const [model, setModel] = useState("gemini_2_5_flash_lite");
  const fileRef = useRef(null);

  const canGenerate = !!docType && !!file && !isGenerating;

  const pickFile = (f) => {
    if (!f) return;
    if (f.type !== "application/pdf") { setFile(null); return; }
    setFile(f);
  };

  const submit = () => {
    if (!canGenerate) return;
    const fd = new FormData();
    fd.append("file", file);
    fd.append("intent", instruction.trim());
    fd.append("document_type", docType || "auto");
    fd.append("engine", "standard_layout");
    fd.append("model", model);
    onGenerate(fd);
  };

  return (
    <div className="text-center relative">
      <div className="absolute -top-[60px] left-1/2 -translate-x-1/2 w-[420px] h-[220px] pointer-events-none z-0"
        style={{ background: "radial-gradient(closest-side, #F7EBF5, rgba(247,235,245,0))", filter: "blur(6px)" }} />
      <div className="relative z-[1]">
        <div className="text-[26px] font-semibold text-[#1A1A1A] tracking-[-0.2px]">
          Generate <span className="bg-clip-text text-transparent" style={{ backgroundImage: "linear-gradient(90deg, #A04A97, #7A4FBF)" }}>Template with AI</span>
        </div>
        <div className={`${TX.helper} !text-[14px] mt-2 leading-[1.55]`}>
          Upload a sample document and describe what you want to extract. AI builds the extraction schema for you.
        </div>
        <button onClick={onManual} className="inline-flex items-center gap-1.5 mt-2.5 text-[12.5px] font-semibold text-[#A04A97] hover:text-[#893F82]">
          <Edit3 size={12} /> Prefer full control? Create a template manually
        </button>
      </div>

      <div className="text-left mt-[30px] relative z-[1]">
        {/* Step 1 — document type */}
        <div className="flex items-center justify-center gap-2.5 mb-3.5">
          <span className="text-[14.5px] font-semibold text-[#1A1A1A]">What type of document are you extracting from?</span>
        </div>
        <div className="flex gap-2.5 flex-wrap justify-center mb-9">
          {STUDIO_DOC_TYPES.map((opt) => (
            <DocTypePill key={opt.id} opt={opt} active={docType === opt.id}
              onClick={() => { setDocType(opt.id); if (PREFILLS[opt.id]) setInstruction(PREFILLS[opt.id]); }} />
          ))}
        </div>

        {/* Step 2 — instruction + sample */}
        <div className="text-[14.5px] font-semibold text-[#1A1A1A] mb-2.5">
          Tell DocuSensa what to extract <span className="font-medium text-[#8A8A90] text-[12.5px]">(optional)</span>
        </div>
        <div className="relative mb-2.5">
          <textarea value={instruction} onChange={(e) => setInstruction(e.target.value)} rows={3}
            placeholder={PLACEHOLDERS[docType || "DEFAULT"]}
            className={`w-full resize-y min-h-[84px] border-[1.5px] rounded-[12px] text-[13.5px] text-[#1A1A1A] leading-[1.55] bg-white outline-none transition-all placeholder:text-[#8A8A90] hover:border-[#A04A97] focus:border-[#A04A97] focus:shadow-[0_0_0_3px_rgba(160,74,151,0.14)] ${instruction ? "border-[#A04A97]" : "border-[#ECE9EE]"}`}
            style={{ padding: docType ? "14px 200px 14px 16px" : "14px 16px" }} />

          <input ref={fileRef} type="file" accept=".pdf,application/pdf" hidden onChange={(e) => pickFile(e.target.files?.[0])} />
          {docType && !file && (
            <button onClick={() => fileRef.current?.click()}
              className="absolute right-2.5 bottom-2.5 flex items-center gap-1.5 px-3 py-1.5 rounded-full border-[1.5px] border-dashed border-[#E9D2E6] bg-[#FBF5FA] text-[#893F82] text-[12px] font-semibold hover:border-[#A04A97] transition-colors">
              <Paperclip size={13} /> Add sample document
            </button>
          )}
          {docType && file && (
            <div className="absolute right-2.5 bottom-2.5 flex items-center gap-2 pl-2.5 pr-2 py-1.5 rounded-full border border-[#ECE9EE] bg-[#FAF9FB] max-w-[220px]">
              <FileText size={13} className="text-[#A04A97] flex-shrink-0" />
              <span className="text-[11.5px] font-semibold text-[#1A1A1A] truncate">{file.name}</span>
              <button onClick={() => setFile(null)} title="Remove"
                className="w-[18px] h-[18px] rounded-full bg-[#F5F5F5] text-[#52525B] flex items-center justify-center flex-shrink-0"><X size={10} /></button>
            </div>
          )}
        </div>
        <div className="flex items-start gap-1.5 text-[12px] text-[#52525B] mb-6 leading-[1.5]">
          <Sparkles size={12} className="text-[#A04A97] mt-0.5 flex-shrink-0" />
          Adding exact fields helps AI extract with more precision — but skip it and AI will automatically detect the relevant fields for you.
        </div>

        {error && (
          <div className="flex items-start gap-2 mb-4 text-[12.5px] text-[#B33A1E] leading-[1.5]">
            <AlertCircle size={14} className="mt-px flex-shrink-0" /> {error}
          </div>
        )}

        {/* Actions */}
        <div className="flex items-center justify-between mt-1.5">
          <AdvancedOptions model={model} setModel={setModel} />
          <button onClick={submit} disabled={!canGenerate}
            className={`${SX.btn} !px-5 !py-2.5 !text-[13.5px] ${canGenerate ? SX.btnPrimary : "bg-[#F5F5F5] text-[#8A8A90] border-none cursor-not-allowed"}`}
            title={!docType ? "Pick a document type" : !file ? "Attach a sample PDF" : undefined}>
            {isGenerating ? <><Spinner size={13} /> Generating…</> : <><Sparkles size={13} /> Generate Template</>}
          </button>
        </div>
        <div className="flex items-center justify-center gap-1.5 text-[12px] text-[#8A8A90] mt-3.5">
          <Lock size={12} /> Your data is secure and never used to train models.
        </div>
      </div>
    </div>
  );
};

export default GenerateCard;
